import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Slider } from 'primereact/slider';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { RadioButton } from 'primereact/radiobutton';
import { Tooltip } from 'primereact/tooltip';
import { Paginator } from 'primereact/paginator';
import courierOrderService, {
    OpenCourierOrderRowDTO,
    OpenCourierOrdersReportResponse,
    ClosedCourierOrdersReportResponse,
} from '../../services/courier-order-report/courier-order-service';
require('./open-courier-orders-reports.scss');

const fmtCurrency = (v: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(v);

const fmtDate = (iso: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};


const OpenCourierOrdersReport: React.FC = () => {
    const toast = useRef<Toast>(null);

    const [keyword, setKeyword] = useState('');
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [customerMobile, setCustomerMobile] = useState('');
    const [daysRange, setDaysRange] = useState<[number, number]>([0, 365]);
    const [reportType, setReportType] = useState<'OPEN' | 'CLOSED'>('OPEN');

    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<number | null>(null);

    const [rows, setRows] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);


    const fetchReport = useCallback(async (pg: number, ps: number, kw: string, fd: Date | null, td: Date | null, dRange: [number, number], rType: 'OPEN' | 'CLOSED', sf?: string | null, so?: number | null, mob?: string) => {
        setLoading(true);
        try {
            if (rType === 'OPEN') {
                const result: OpenCourierOrdersReportResponse =
                    await courierOrderService.getOpenCourierOrdersReport({
                        fromDate: fd,
                        toDate: td,
                        keyword: kw.trim() || undefined,
                        customerMobile: mob?.trim() || undefined,
                        minDays: dRange[0],
                        maxDays: dRange[1],
                        sortField: sf ?? undefined,
                        sortOrder: so ?? undefined,
                        pageNumber: pg,
                        pageSize: ps,
                        reportType: rType,
                    });
                setRows(result.orders ?? []);
                setTotalRecords(result.totalRecords ?? 0);
            } else {
                const result: ClosedCourierOrdersReportResponse =
                    await courierOrderService.getClosedCourierOrdersReport({
                        fromDate: fd,
                        toDate: td,
                        keyword: kw.trim() || undefined,
                        customerMobile: mob?.trim() || undefined,
                        minDays: dRange[0],
                        maxDays: dRange[1],
                        sortField: sf ?? undefined,
                        sortOrder: so ?? undefined,
                        pageNumber: pg,
                        pageSize: ps,
                        reportType: rType,
                    });
                setRows(result.invoices ?? []);
                setTotalRecords(result.totalRecords ?? 0);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load report', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReport(pageNumber, pageSize, keyword, fromDate, toDate, daysRange, reportType, sortField, sortOrder, customerMobile);
    }, [pageNumber, pageSize, sortField, sortOrder]); 

    useEffect(() => {
        const timer = setTimeout(() => {
            setPageNumber(1);
            fetchReport(1, pageSize, keyword, fromDate, toDate, daysRange, reportType, sortField, sortOrder, customerMobile);
        }, 500);
        return () => clearTimeout(timer);
    }, [keyword, customerMobile]); 


    useEffect(() => {
        setPageNumber(1);
        fetchReport(1, pageSize, keyword, fromDate, toDate, daysRange, reportType, sortField, sortOrder, customerMobile);
    }, [fromDate, toDate, daysRange, reportType]); 


    const handleReset = () => {
        setFromDate(null);
        setToDate(null);
        setKeyword('');
        setCustomerMobile('');
        setDaysRange([0, 365]);
        setReportType('OPEN');
        setSortField(null);
        setSortOrder(null);
        setPageNumber(1);
        fetchReport(1, pageSize, '', null, null, [0, 365], 'OPEN', null, null, '');
    };

    const handlePageChange = (e: any) => {
        setPageNumber(e.page + 1);
        setPageSize(e.rows);
    };

    const handleExport = async () => {
        setLoading(true);
        try {
            let headers: string[] = [];
            let csvRows: string[] = [];
            let fileName = '';

            if (reportType === 'OPEN') {
                const all: OpenCourierOrdersReportResponse = await courierOrderService.getOpenCourierOrdersReport({
                    fromDate, toDate,
                    keyword: keyword.trim() || undefined,
                    minDays: daysRange[0],
                    maxDays: daysRange[1],
                    pageNumber: 1,
                    pageSize: 99999,
                    reportType: 'OPEN',
                });
                const data = all.orders ?? [];
                if (!data.length) {
                    toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Nothing to export', life: 2000 });
                    return;
                }
                headers = ['Order #', 'Order Date', 'Order Value', 'Customer Name', 'Customer Mobile #', 'Ordered By Name', 'Ordered By Mobile', 'Courier Provider'];
                csvRows = data.map(r => [
                    r.orderID,
                    fmtDate(r.orderDate),
                    r.grossAmount,
                    r.customerName ?? '',
                    r.customerMobile ?? '',
                    r.createdByName ?? '',
                    r.createdByMobile ?? '',
                    r.courierProviderName ?? '',
                ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
                fileName = `open-courier-orders-${new Date().toISOString().slice(0, 10)}.csv`;
            } else {
                const all: ClosedCourierOrdersReportResponse = await courierOrderService.getClosedCourierOrdersReport({
                    fromDate, toDate,
                    keyword: keyword.trim() || undefined,
                    minDays: daysRange[0],
                    maxDays: daysRange[1],
                    pageNumber: 1,
                    pageSize: 99999,
                    reportType: 'CLOSED',
                });
                const data = all.invoices ?? [];
                if (!data.length) {
                    toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Nothing to export', life: 2000 });
                    return;
                }
                headers = ['Order #', 'Order Date', 'Order Value', 'Customer Name', 'Customer Mobile #', 'Ordered By Name', 'Ordered By Mobile', 'Courier Provider', 'Invoice No', 'Tracking Number', 'Invoice Date', 'Invoice Value'];
                csvRows = data.map(r => [
                    r.orderID,
                    fmtDate(r.orderDate),
                    r.orderValue,
                    r.customerName ?? '',
                    r.customerMobile ?? '',
                    r.createdByName ?? '',
                    r.createdByMobile ?? '',
                    r.courierProviderName ?? '',
                    r.invoiceNo,
                    r.trackingNumber ?? '',
                    fmtDate(r.invoiceDate),
                    r.invoiceValue,
                ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
                fileName = `closed-courier-orders-${new Date().toISOString().slice(0, 10)}.csv`;
            }

            const csv = [headers.join(','), ...csvRows].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
            toast.current?.show({ severity: 'success', summary: 'Exported', detail: `Records downloaded`, life: 2000 });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Export Failed', detail: 'Could not export data', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const orderIdTemplate = (r: OpenCourierOrderRowDTO) => (
        <span className="ocor-order-id">#{r.orderID}</span>
    );

    const orderDateTemplate = (r: OpenCourierOrderRowDTO) => (
        <span>{fmtDate(r.orderDate)}</span>
    );

    const grossTemplate = (r: OpenCourierOrderRowDTO) => (
        <span className="ocor-amount">{fmtCurrency(r.grossAmount)}</span>
    );



    const mobileHeaderTemplate = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>Customer Mobile #</span>
            <InputText
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
                placeholder="Search by phone..."
                className="p-inputtext-sm"
                style={{ width: '100%', borderRadius: '4px', fontWeight: 'normal' }}
            />
        </div>
    );

    return (
        <div className="ocor-root">
            <Toast ref={toast} />

            <div className="ocor-top-card">
                
                <div className="ocor-top-card-header">
                    <div className="ocor-page-header-icon">
                        <i className="pi pi-truck" />
                    </div>
                    <div className="ocor-page-header-text">
                        <div className="ocor-page-header-title">Courier Orders</div>
                        <div className="ocor-page-header-sub">
                            Track and manage open orders with courier options
                        </div>
                    </div>
                    <div className="ocor-header-options">
                        <div className="ocor-radio-group">
                            <div className="ocor-radio-item">
                                <RadioButton inputId="openOrder" name="reportType" value="OPEN" onChange={(e) => setReportType(e.value)} checked={reportType === 'OPEN'} />
                                <label htmlFor="openOrder">Open Order</label>
                            </div>
                            <div className="ocor-radio-item">
                                <RadioButton inputId="closedOrder" name="reportType" value="CLOSED" onChange={(e) => setReportType(e.value)} checked={reportType === 'CLOSED'} />
                                <label htmlFor="closedOrder">Closed Order</label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ocor-filter-panel-title">
                    <i className="pi pi-filter" />
                    <span>Filter Open Courier Orders</span>
                </div>
                <div className="ocor-filter-row">
        
                    <div className="ocor-search-wrap">
                        <i className="pi pi-search ocor-search-icon" />
                        <InputText
                            value={keyword}
                            onChange={e => setKeyword(e.target.value)}
                            placeholder="Keyword Search"
                            className="ocor-search-input"
                        />
                    </div>

                    <div className="ocor-date-wrap">
                        <Calendar
                            value={fromDate}
                            onChange={e => setFromDate(e.value as Date)}
                            dateFormat="dd-mm-yy"
                            showIcon
                            placeholder="From Date"
                            showButtonBar
                            className="ocor-calendar"
                        />
                    </div>

                    <div className="ocor-date-wrap">
                        <Calendar
                            value={toDate}
                            onChange={(e: any) => setToDate(e.value as Date)}
                            dateFormat="dd-mm-yy"
                            showIcon
                            placeholder="To Date"
                            showButtonBar
                            className="ocor-calendar"
                        />
                    </div>

                    <div className="ocor-custom-slider-section">
                        <div className="ocor-custom-slider-label">
                            Days Since Last Ordered: <span>{daysRange[0]} - {daysRange[1]} days</span>
                        </div>
                        <Slider
                            value={daysRange}
                            onChange={(e: any) => setDaysRange(e.value as [number, number])}
                            range
                            min={0}
                            max={365}
                            className="ocor-premuim-slider"
                        />
                    </div>

                    <Button
                        icon="pi pi-refresh"
                        label="Reset"
                        className="ocor-btn-reset"
                        onClick={handleReset}
                    />
                </div>
            </div>

            <div className="ocor-table-section">
                <div className="ocor-table-section-header">
                    <div className="ocor-section-title">
                        <i className="pi pi-list" />
                        <span>Courier Records ({totalRecords})</span>
                    </div>
                    <div className="ocor-section-actions">
                        <Tooltip target=".ocor-export-icon-btn" content="Export CSV" position="left" />
                        <FileActionButton action="export"
                            icon="pi pi-download"
                            className="p-button-text p-button-rounded ocor-export-icon-btn"
                            onClick={handleExport}
                            loading={loading}
                            disabled={!rows.length}
                        />
                    </div>
                </div>

                <div className="ocor-table-card">
                    <DataTable
                        value={rows}
                        loading={loading}
                        emptyMessage="No open courier orders found."
                        scrollable
                        scrollHeight="520px"
                        className="ocor-datatable"
                        rowHover
                        lazy
                        sortField={sortField || undefined}
                        sortOrder={sortOrder as any}
                        onSort={(e: any) => {
                            setSortField(e.sortField);
                            setSortOrder(e.sortOrder);
                        }}
                    >
                        {reportType === 'OPEN' && [
                            <Column key="orderID" field="orderID" header="Order #" body={orderIdTemplate} style={{ minWidth: '105px' }} sortable />,
                            <Column key="orderDate" field="orderDate" header="Order Date" body={orderDateTemplate} style={{ minWidth: '130px' }} sortable />,
                            <Column key="grossAmount" field="grossAmount" header="Order Value" body={grossTemplate} style={{ minWidth: '130px' }} sortable />,
                            <Column key="customerName" field="customerName" header="Customer Name" style={{ minWidth: '160px' }} sortable />,
                            <Column key="customerMobile" field="customerMobile" header={mobileHeaderTemplate} style={{ minWidth: '180px' }} />,
                            <Column key="createdByName" field="createdByName" header="Ordered By Name" />,
                            <Column key="createdByMobile" field="createdByMobile" header="Ordered By Mobile" />,
                            <Column key="courierProviderName" field="courierProviderName" header="Courier Provider" style={{ minWidth: '160px' }} />
                        ]}
                        {reportType === 'CLOSED' && [
                            <Column key="orderID" field="orderID" header="Order #" body={orderIdTemplate} sortable />,
                            <Column key="orderDate" field="orderDate" header="Order Date" body={orderDateTemplate} sortable />,
                            <Column key="orderValue" field="orderValue" header="Order Value" body={(r: any) => fmtCurrency(r.orderValue)} sortable />,
                            <Column key="customerName" field="customerName" header="Customer Name" sortable />,
                            <Column key="customerMobile" field="customerMobile" header={mobileHeaderTemplate} style={{ minWidth: '180px' }} />,
                            <Column key="createdByName" field="createdByName" header="Ordered By Name" />,
                            <Column key="createdByMobile" field="createdByMobile" header="Ordered By Mobile" />,
                            <Column key="courierProviderName" field="courierProviderName" header="Courier Provider" />,
                            <Column key="invoiceNo" field="invoiceNo" header="Invoice No" sortable />,
                            <Column key="trackingNumber" field="trackingNumber" header="Tracking Number" sortable />,
                            <Column key="invoiceDate" field="invoiceDate" header="Invoice Date" body={(r: any) => fmtDate(r.invoiceDate)} sortable />,
                            <Column key="invoiceValue" field="invoiceValue" header="Invoice Value" body={(r: any) => fmtCurrency(r.invoiceValue)} sortable />
                        ]}
                    </DataTable>

                    <Paginator
                        first={(pageNumber - 1) * pageSize}
                        rows={pageSize}
                        totalRecords={totalRecords}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        onPageChange={handlePageChange}
                        template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                        className="ocor-paginator"
                    />
                </div>
            </div>
        </div>
    );
};

export default OpenCourierOrdersReport;
